// TV support: polyfills for old Chromium (Tizen 4 = Chrome 56), D-pad spatial navigation,
// remote-control keys (Samsung Tizen / LG webOS) and media capability detection.

// ---- polyfills (syntax is lowered by esbuild; these are the missing runtime APIs) ----

// Before Chrome 72 (Tizen 4-5.5 = Chromium 56-69) fetch() defaulted to credentials: "omit": the login
// cookie was neither stored nor sent -> "signed in" but every next request unauthenticated.
// Make same-origin the default everywhere, like modern browsers.
if (typeof window !== "undefined" && window.fetch) {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) =>
    nativeFetch(input as RequestInfo, { credentials: "same-origin", ...(init || {}) })) as typeof fetch;
}
if (!String.prototype.padStart) {
  String.prototype.padStart = function (len: number, fill = " ") {
    let s = String(this);
    while (s.length < len) s = fill + s;
    return s.slice(s.length - Math.max(len, String(this).length));
  };
}
if (!(Element.prototype as any).closest) {
  (Element.prototype as any).closest = function (sel: string) {
    let el: Element | null = this;
    while (el && !el.matches(sel)) el = el.parentElement;
    return el;
  };
}

/** LG webOS (the TV browser says "NetCast" / "Web0S") */
export const isWebOS = /Web0S|webOS|NetCast/i.test(navigator.userAgent);
export const isTV = /Tizen|SMART-TV|SmartTV|Web0S|webOS|NetCast|HbbTV|AFT|BRAVIA|CrKey/i.test(navigator.userAgent);

// key codes: arrows 37-40, OK/Enter 13; Tizen: Back 10009, Play 415, Pause 19, PlayPause 10252,
// Stop 413, FF 417, RW 412. webOS: Back 461.
export const KEY = {
  LEFT: 37, UP: 38, RIGHT: 39, DOWN: 40, ENTER: 13, BACKSPACE: 8, ESC: 27,
  BACK: [10009, 461], PLAY: 415, PAUSE: 19, PLAYPAUSE: 10252, STOP: 413, FF: 417, RW: 412, SPACE: 32,
};

/** Register media keys when running as a packaged Tizen app (no-op in the browser). */
export function registerTizenKeys() {
  try {
    const tizen = (window as any).tizen;
    for (const k of ["MediaPlayPause", "MediaPlay", "MediaPause", "MediaStop", "MediaFastForward", "MediaRewind"]) {
      tizen?.tvinputdevice?.registerKey(k);
    }
  } catch {}
}

// ---- player hooks: the watch page registers these while it's open ----
export interface PlayerKeys {
  toggle(): void;
  seekBy(sec: number): void;
  video: HTMLVideoElement;
  seekBar: HTMLElement;
}
let player: PlayerKeys | null = null;
export const setPlayerKeys = (p: PlayerKeys | null) => (player = p);

// ---- spatial navigation ----
const FOCUSABLE = 'a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"]), video';

function visible(el: HTMLElement) {
  if (el.hidden || el.closest("[hidden]")) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function candidates(): HTMLElement[] {
  return Array.prototype.slice.call(document.querySelectorAll(FOCUSABLE)).filter(visible);
}

function moveFocus(dir: "left" | "right" | "up" | "down") {
  const cur = document.activeElement as HTMLElement | null;
  const all = candidates();
  if (!cur || cur === document.body || all.indexOf(cur) < 0) {
    all[0]?.focus();
    return true;
  }
  const a = cur.getBoundingClientRect();
  const ax = a.left + a.width / 2;
  const ay = a.top + a.height / 2;
  let best: HTMLElement | null = null;
  let bestScore = Infinity;
  for (const el of all) {
    if (el === cur || cur.contains(el) || el.contains(cur)) continue;
    const b = el.getBoundingClientRect();
    const bx = b.left + b.width / 2;
    const by = b.top + b.height / 2;
    let main: number, cross: number;
    // edge-based "is it in that direction" + distance, cross-axis offset weighted heavier
    if (dir === "right") { if (b.left < a.right - 5) continue; main = b.left - a.right; cross = Math.abs(by - ay); }
    else if (dir === "left") { if (b.right > a.left + 5) continue; main = a.left - b.right; cross = Math.abs(by - ay); }
    else if (dir === "down") { if (b.top < a.bottom - 5) continue; main = b.top - a.bottom; cross = Math.abs(bx - ax); }
    else { if (b.bottom > a.top + 5) continue; main = a.top - b.bottom; cross = Math.abs(bx - ax); }
    // vertical moves: prefer overlapping columns; horizontal moves: prefer same row
    const score = main + cross * (dir === "up" || dir === "down" ? 0.5 : 3);
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }
  if (!best) return false;
  best.focus();
  const anyBest = best as any;
  if (anyBest.scrollIntoViewIfNeeded) anyBest.scrollIntoViewIfNeeded(false);
  else best.scrollIntoView(false);
  // keep headers visible when moving up to the first row
  if (dir === "up" && best.getBoundingClientRect().top < 80) window.scrollBy(0, -80);
  return true;
}

function isTyping(el: Element | null) {
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
}

export function installKeys(back: () => void) {
  registerTizenKeys();
  if (isTV) document.body.classList.add("tv");

  document.addEventListener("keydown", (e) => {
    if (e.defaultPrevented) return; // handled by the element itself
    const k = e.keyCode;
    const active = document.activeElement as HTMLElement | null;
    const typing = isTyping(active);

    // Back: leave the text box first, otherwise go back in history
    if (KEY.BACK.indexOf(k) >= 0 || (k === KEY.BACKSPACE && !typing) || (k === KEY.ESC && !typing)) {
      e.preventDefault();
      if (typing && active) active.blur();
      else back();
      return;
    }

    // media keys / player controls
    if (player) {
      const onPlayer = !active || active === document.body || active === player.video || active === player.seekBar;
      if (k === KEY.PLAYPAUSE || k === KEY.PLAY || k === KEY.PAUSE || ((k === KEY.ENTER || k === KEY.SPACE) && onPlayer)) {
        e.preventDefault();
        if (k === KEY.PLAY && !player.video.paused) return;
        if (k === KEY.PAUSE && player.video.paused) return;
        player.toggle();
        return;
      }
      if (k === KEY.FF) return e.preventDefault(), player.seekBy(60);
      if (k === KEY.RW) return e.preventDefault(), player.seekBy(-30);
      if (k === KEY.STOP) return e.preventDefault(), back();
      if (onPlayer && (k === KEY.LEFT || k === KEY.RIGHT)) {
        e.preventDefault();
        player.seekBy(k === KEY.RIGHT ? 30 : -10);
        return;
      }
    }

    // D-pad navigation
    const dir = k === KEY.LEFT ? "left" : k === KEY.RIGHT ? "right" : k === KEY.UP ? "up" : k === KEY.DOWN ? "down" : null;
    if (dir) {
      // inside a text box, left/right move the caret; selects use up/down natively
      if (typing && (dir === "left" || dir === "right")) return;
      if (active?.tagName === "SELECT" && (dir === "up" || dir === "down") && !isTV) return;
      if (moveFocus(dir)) e.preventDefault();
      return;
    }

    // OK on non-native "buttons" (cards with role=button, tabindex divs)
    if (k === KEY.ENTER && active && active.getAttribute("role") === "button") {
      e.preventDefault();
      active.click();
    }
  });
}

/** Which codecs the device can decode itself (sent to the server so it can avoid transcoding). */
export function mediaCaps(): string {
  const v = document.createElement("video");
  const ok = (t: string) => {
    try {
      const ms = (window as any).MediaSource;
      return v.canPlayType(t) !== "" || (ms && ms.isTypeSupported && ms.isTypeSupported(t));
    } catch {
      return false;
    }
  };
  const caps: string[] = [];
  if (ok('video/mp4; codecs="hvc1.1.6.L93.B0"') || ok('video/mp4; codecs="hev1.1.6.L93.B0"')) caps.push("hevc");
  if (ok('audio/mp4; codecs="ac-3"')) caps.push("ac3");
  if (ok('audio/mp4; codecs="ec-3"')) caps.push("eac3");
  // user preference on this device: keep Dolby Atmos (only meaningful when E-AC-3 can be passed through)
  if (caps.includes("eac3") && prefersAtmos()) caps.push("atmos");
  return caps.join(",");
}

export const ATMOS_KEY = "ts.atmos";
export function prefersAtmos() {
  try {
    return localStorage.getItem(ATMOS_KEY) === "1";
  } catch {
    return false;
  }
}
/** can this device pass Dolby Digital Plus (and so DD+ Atmos) through? */
export function canEac3() {
  const v = document.createElement("video");
  try {
    const ms = (window as any).MediaSource;
    return v.canPlayType('audio/mp4; codecs="ec-3"') !== "" || !!(ms && ms.isTypeSupported && ms.isTypeSupported('audio/mp4; codecs="ec-3"'));
  } catch {
    return false;
  }
}
